class CollectionSamples extends HTMLElement {
  constructor() {
    super();

    // Constants
    this.STORAGE_KEY = this.dataset.samplesLocalstorageKey || "chosen_samples";
    this.MAX_SAMPLES = this.dataset.samplesMaxCount || 5;
    this.UNLIMITED_SAMPLES = this.dataset.unlimitedSamples === "true" || "false";
    this.MAX_SAMPLES_TEXT = this.dataset.maxSamplesText;
    this.SAMPLES_TAG_TO_CLEAR = this.dataset.samplesTagToClear;
    this.EXPRESS_DELIVERY_LABEL = this.dataset.expressDeliveryLabel;
    this.EXPRESS_DELIVERY_FROM_TS = this.dataset.expressDeliveryFromTs;
    this.EXPRESS_DELIVERY_TO_TS = this.dataset.expressDeliveryToTs;

    this.fabricTypeFilters = this.querySelectorAll("[data-fabric-type-filter]");
    this.fabricDescriptions = this.querySelectorAll("[data-fabric-description]");
    this.toggleFilters = this.querySelector("[data-toggle-filters]");
    this.fabricSearchInput = this.querySelector("[data-fabric-search-input]");
    this.checkboxHeadings = this.querySelectorAll("[data-checkbox-heading]");
    this.checkboxFilters = this.querySelectorAll("[data-checkbox-filters]");
    this.checkboxItems = this.querySelectorAll("[data-checkbox]");
    this.clearAllFilters = this.querySelector("[data-clear-all-filters]");
    this.clearFilters = this.querySelectorAll("[data-clear-filters]");
    this.fabricSamplesGrid = this.querySelector("[data-fabric-samples-grid]");
    this.fabricSamples = this.querySelectorAll("[data-fabric-sample]");
    this.noResults = this.querySelector("[data-no-results]");
    this.chosenSamplesHld = this.querySelector("[data-chosen-samples]");
    this.chosenSamplesList = this.querySelector("[data-chosen-samples-list]");
    this.toggleSampleButtons = this.querySelectorAll("[data-toggle-sample]");
    this.addSamplesToCartButton = this.querySelector("[data-add-samples-to-cart]");
  }

  eventListeners() {
    this.fabricTypeFilters.forEach(filter => {
      filter.addEventListener("click", () => {
        this.fabricTypeFilters.forEach(filter => {
          filter.setAttribute("data-active", "false");
        });
        filter.setAttribute("data-active", "true");

        this.fabricSamplesGrid.setAttribute("data-fabric-type", filter.dataset.fabricTypeFilter);

        this.fabricDescriptions.forEach(description => {
          description.setAttribute("data-is-visible", "false");
          if (description.dataset.fabricDescription === filter.dataset.fabricTypeFilter) {
            description.setAttribute("data-is-visible", "true");
          }
        });

        this.handleFabricSamplesVisibility();
      });
    });

    if (this.fabricSearchInput) {
      this.fabricSearchInput.addEventListener("input", () => {
        this.handleFabricSamplesVisibility();
      });
    }

    if (this.checkboxItems) {
      this.checkboxItems.forEach(item => {
        item.addEventListener("change", () => {
          this.handleCheckboxChange();
          this.handleFabricSamplesVisibility();
        });
      });
    }

    if (this.clearAllFilters) {
      this.clearAllFilters.addEventListener("click", () => {
        this.checkboxItems.forEach(item => {
          item.checked = false;
        });
        this.handleCheckboxChange();
        this.handleFabricSamplesVisibility();
      });
    }

    if (this.clearFilters) {
      this.clearFilters.forEach(clearBtn => {
        clearBtn.addEventListener("click", () => {
          const parentFilter = clearBtn.closest("[data-checkbox-filters]");
          const checkboxesInParent = parentFilter.querySelectorAll("[data-checkbox]");
          checkboxesInParent.forEach(item => {
            item.checked = false;
          });
          this.handleCheckboxChange();
          this.handleFabricSamplesVisibility();
        });
      });
    }

    if (this.checkboxHeadings) {
      this.checkboxHeadings.forEach(heading => {
        heading.addEventListener("click", () => {
          const isActive = heading.getAttribute("data-is-active") === "true";
          heading.setAttribute("data-is-active", isActive ? "false" : "true");
        });
      });
    }

    if (this.toggleSampleButtons) {
      this.toggleSampleButtons.forEach(button => {
        button.addEventListener("click", () => {
          this.toggleSample(button.dataset.toggleSample);
        });
      });
    }

    if (this.addSamplesToCartButton) {
      this.addSamplesToCartButton.addEventListener("click", () => {
        this.addSamplesToCart();
      });
    }
  }

  async addSamplesToCart() {
    // Get product IDs from localStorage
    let chosenSamples = [];
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        chosenSamples = JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return;
    }

    if (chosenSamples.length === 0) {
      return;
    }

    // Disable the button to prevent double clicks
    if (this.addSamplesToCartButton) {
      this.chosenSamplesHld.setAttribute("data-is-loading", "true");
      this.addSamplesToCartButton.disabled = true;
      this.addSamplesToCartButton.toggleAttribute("data-loading", true);
    }

    try {
      // Get variant IDs for each product
      const items = [];

      for (const variantId of chosenSamples) {
        const item = {
          id: variantId,
          quantity: 1
        };

        // Samples always ship as express delivery
        if (this.EXPRESS_DELIVERY_LABEL) {
          item.properties = {
            "_Delivery time": this.EXPRESS_DELIVERY_LABEL,
            "_Lead Time From": this.EXPRESS_DELIVERY_FROM_TS,
            "_Lead Time Timestamp": this.EXPRESS_DELIVERY_TO_TS
          };
        }

        items.push(item);
      }

      if (items.length === 0) {
        throw new Error("No valid items to add to cart");
      }

      // remove from the cart all cart items with a set tag
      const cart = await (await fetch("/cart.js")).json();
      const updates = {};
      const uniqueHandles = [...new Set(cart.items.map(item => item.handle))];
      const handleToTags = {};

      await Promise.all(
        uniqueHandles.map(async handle => {
          try {
            const product = await (await fetch(`/products/${handle}.js`)).json();
            handleToTags[handle] = product.tags || [];
          } catch (e) {
            handleToTags[handle] = [];
          }
        })
      );

      cart.items.forEach(item => {
        if (handleToTags[item.handle]?.includes(this.SAMPLES_TAG_TO_CLEAR)) {
          updates[item.key] = 0;
        }
      });

      let clearCartResponse = { ok: true };
      if (Object.keys(updates).length > 0) {
        clearCartResponse = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ updates })
        });
      }

      if (!clearCartResponse.ok) {
        throw new Error("Failed to clear cart");
      }

      // Add all items to cart
      const addCartResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ items })
      });

      if (!addCartResponse.ok) {
        throw new Error("Failed to add items to cart");
      }

      window.dispatchEvent(
        new CustomEvent("cart::update", {
          detail: {
            cartMessage: "samples_added"
          }
        })
      );

      // Clear the chosen samples from localStorage
      // localStorage.removeItem(this.STORAGE_KEY); // don't clear the localStorage to keep the chosen samples for the next time

      // hide chosen samples list
      this.chosenSamplesHld.setAttribute("data-is-visible", "false");

      // Update the UI
      // this.renderSamples();
    } catch (error) {
      console.error("Error adding samples to cart:", error);
    }

    // Re-enable the button (Safari-compatible)
    if (this.addSamplesToCartButton) {
      this.chosenSamplesHld.setAttribute("data-is-loading", "false");
      this.addSamplesToCartButton.disabled = false;
      this.addSamplesToCartButton.removeAttribute("data-loading");
    }
  }

  handleCheckboxChange() {
    this.checkboxFilters.forEach(filter => {
      const checkboxList = filter.querySelector("[data-checkbox-list]");
      const checkboxItems = checkboxList.querySelectorAll("[data-checkbox]:checked");
      const checkboxCount = checkboxItems.length;
      filter.querySelector("[data-count]").textContent = `(${checkboxCount})`;
      filter.querySelector("[data-count]").setAttribute("data-count", checkboxCount.toString());
      filter.querySelector("[data-clear-filters]").setAttribute("data-is-visible", checkboxCount > 0 ? "true" : "false");
    });

    // Scroll to the fabric samples grid if it's not visible
    if (this.fabricSamplesGrid) {
      const rect = this.fabricSamplesGrid.getBoundingClientRect();
      if (rect.top < 0) {
        this.fabricSamplesGrid.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    // Update clear all filters button visibility if any checkbox is checked
    let hasCheckedFilters = false;
    this.checkboxItems.forEach(item => {
      if (item.checked) {
        hasCheckedFilters = true;
      }
    });
    this.clearAllFilters.setAttribute("data-is-visible", hasCheckedFilters ? "true" : "false");
  }

  handleFabricSamplesVisibility() {
    // Get checked filters
    const checkedFeatures = Array.from(this.querySelectorAll('[data-checkbox-filters="features"] [data-checkbox]:checked')).map(cb => cb.value);
    const checkedColors = Array.from(this.querySelectorAll('[data-checkbox-filters="colors"] [data-checkbox]:checked')).map(cb => cb.value);

    let noResultsVisible = true;

    this.fabricSamples.forEach(sample => {
      let isVisible = false;
      let isFabricTypeVisible = false;
      let isSearchVisible = false;
      let isFeaturesVisible = false;
      let isColorVisible = false;

      // Check fabric type
      if (this.fabricSamplesGrid.dataset.fabricType === "all" || sample.dataset.fabricType === this.fabricSamplesGrid.dataset.fabricType) {
        isFabricTypeVisible = true;
      }

      // Check search
      if (this.fabricSearchInput.value.length > 0) {
        if (sample.dataset.fabricSample.toLowerCase().includes(this.fabricSearchInput.value.toLowerCase())) {
          isSearchVisible = true;
        }
      } else {
        isSearchVisible = true;
      }

      // Check features
      if (checkedFeatures.length > 0) {
        const sampleFeatures = sample.dataset.features ? sample.dataset.features.split(",") : [];
        isFeaturesVisible = checkedFeatures.every(feature => sampleFeatures.includes(feature));
      } else {
        isFeaturesVisible = true;
      }

      // Check colors
      if (checkedColors.length > 0) {
        const sampleColor = sample.dataset.color || "";
        isColorVisible = checkedColors.includes(sampleColor);
      } else {
        isColorVisible = true;
      }

      if (isFabricTypeVisible && isSearchVisible && isFeaturesVisible && isColorVisible) {
        isVisible = true;
        noResultsVisible = false;
      }

      sample.setAttribute("data-is-visible", isVisible ? "true" : "false");
    });

    this.noResults.setAttribute("data-is-visible", noResultsVisible ? "true" : "false");
  }

  async toggleSample(sampleId) {
    // Get the count of samples already in cart
    const availableSlots = this.UNLIMITED_SAMPLES === "true" ? Infinity : this.MAX_SAMPLES;

    // Get current list from localStorage
    let chosenSamples = [];
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        chosenSamples = JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error reading from localStorage:", error);
    }

    // Check if sample is already in the list
    const index = chosenSamples.indexOf(sampleId);

    if (index !== -1) {
      // Remove it from the list
      chosenSamples.splice(index, 1);
    } else {
      // Check if we can add more samples
      if (chosenSamples.length >= availableSlots) {
        window.dispatchEvent(new CustomEvent("header-message", { detail: { message: this.MAX_SAMPLES_TEXT } }));
        return;
      }

      chosenSamples.push(sampleId);
    }

    // Save back to localStorage
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(chosenSamples));
    } catch (error) {
      console.error("Error writing to localStorage:", error);
    }

    // Update the UI
    this.renderSamples();
  }

  async renderSamples() {
    // Get the count of samples already in cart
    const availableSlots = this.UNLIMITED_SAMPLES === "true" ? Infinity : this.MAX_SAMPLES;

    // Get current list from localStorage
    let chosenSamples = [];
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        chosenSamples = JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error reading from localStorage:", error);
    }

    // Update all data-chosen-sample elements
    const chosenSampleElements = this.querySelectorAll("[data-chosen-sample]");
    chosenSampleElements.forEach(element => {
      const sampleId = element.dataset.chosenSample;
      const isChosen = chosenSamples.includes(sampleId);
      element.setAttribute("data-is-visible", isChosen ? "true" : "false");

      // Update parent grid item's data-is-chosen attribute
      const gridItem = element.closest("[data-fabric-sample]");
      if (gridItem) {
        gridItem.setAttribute("data-is-chosen", isChosen ? "true" : "false");
      }
    });

    // Update the chosen samples list
    if (this.chosenSamplesList) {
      // Create a document fragment to build the new list off-DOM
      const fragment = document.createDocumentFragment();
      const loadedPromises = [];

      chosenSamples.forEach(sampleId => {
        const sampleElement = this.querySelector(`[data-chosen-sample="${sampleId}"]`);
        if (sampleElement) {
          const li = document.createElement("li");

          // Clone the element to preserve cached images
          const clonedContent = sampleElement.cloneNode(true);

          // Ensure image is loaded before showing to prevent flickering
          const img = clonedContent.querySelector("img");
          if (img && !img.complete) {
            loadedPromises.push(
              new Promise(resolve => {
                img.onload = resolve;
                img.onerror = resolve;
                // Fallback timeout in case image never loads
                setTimeout(resolve, 1000);
              })
            );
          }

          li.appendChild(clonedContent);

          // Re-bind the remove button
          const removeBtn = li.querySelector("[data-remove-sample]");
          if (removeBtn) {
            removeBtn.addEventListener("click", () => {
              this.toggleSample(sampleId);
            });
          }

          fragment.appendChild(li);
        }
      });

      // Add empty li elements based on available slots (max samples - samples in cart)
      if (this.UNLIMITED_SAMPLES === "false") {
        const emptySlots = Math.max(0, availableSlots - chosenSamples.length);
        for (let i = 0; i < emptySlots; i++) {
          const li = document.createElement("li");
          fragment.appendChild(li);
        }
      }

      // Wait for all images to be ready before updating the DOM
      if (loadedPromises.length > 0) {
        try {
          await Promise.all(loadedPromises);
        } catch (error) {
          console.error("Error waiting for images to load:", error);
        }
      }

      // Update the DOM all at once
      this.chosenSamplesList.innerHTML = "";
      this.chosenSamplesList.appendChild(fragment);
    }

    // Update chosen samples holder visibility
    if (this.chosenSamplesHld) {
      this.chosenSamplesHld.setAttribute("data-is-visible", chosenSamples.length > 0 ? "true" : "false");
    }
  }

  init() {
    this.eventListeners();
    this.renderSamples();
  }

  connectedCallback() {
    this.init();
  }
}

if (!customElements.get("collection-samples")) customElements.define("collection-samples", CollectionSamples);
